import React, { useState } from "react";
import Highcharts, { color } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
    Box,
    MenuItem,
    Select,
    Skeleton,
    Typography,
    useTheme,
    Grid2
} from "@mui/material";

import { IoIosArrowDown } from "react-icons/io";
import { useParams } from "react-router-dom";
import { useGetAllPlanActuaDataQuery } from "store/slices/apis/procurementSlice";
import { fontWeight, style } from "@mui/system";
import { enabled } from "store";
import { ReactComponent as ChevronDownIcon } from "assets/PwC_Funct_Icons_ChevronDown_Outline_Black_RGB.svg"

const PlanActualProcurement = () => {
    const theme = useTheme();
    const { projectId } = useParams();

    const [selectedCategory, setSelectedCategory] = useState("All Categories");

    const { data, isLoading, error } = useGetAllPlanActuaDataQuery({ projectId });


    const rawArray = data
        ? Array.isArray(data)
            ? data
            : [data]
        : [];


    const allCategories = [...new Set(rawArray.map(item => item.materialCategory))];


    const filteredData = selectedCategory === "All Categories"
        ? rawArray
        : rawArray.filter(item => item.materialCategory === selectedCategory);


    const categories = filteredData.map(item => item.materialCategory);
    const planData = filteredData.map(item => item.planQuantity);
    const actualData = filteredData.map(item => item.actualQuantity);

    const isAllZero = filteredData.length > 0 && filteredData.every(
        item => item.planQuantity === 0 && item.actualQuantity === 0
    );

    const chartOptions = {
        chart: {
            type: "column",
            height: 450,
            spacingLeft: 0,
            spacingRight: 0,
            spacingTop: 0,
            spacingBottom: 0,
        },

        title: { text: "" },
        xAxis: {
            categories,
            tickmarkPlacement: "on",
            tickWidth: 1,
            tickLength: 10,
            tickColor: "#7581952E",
            lineWidth: 1,
            lineColor: "#758195",
            labels: {
                style: {
                    fontWeight: 500,
                    fontSize: "10px",
                    color: "#414D55",
                    opacity: 0.5,
                    lineHeight: "100%",
                },
            },
        },

        yAxis: {
            min: 0,
            gridLineWidth: 1,
            tickWidth: 1,
            tickLength: 10,
            tickColor: "#7581952E",
            lineWidth: 1,
            lineColor: "#758195",
            title: {
                text: "Quantity",
                style: {
                    color: "#415385",
                    fontWeight: "500",
                    fontSize: "14px",
                },
            },
            labels: {
                style: {
                    fontWeight: 500,
                    fontSize: "10px",
                    color: "#414D55",
                    opacity: 0.5,
                    lineHeight: "100%",
                },
            },

        },
        colors: ["#295477", "#E72B33"],
        plotOptions: {
            column: {
                stacking: 'normal',
                dataLabels: {
                    enabled: true
                }
            }
        },

        series: [
            {
                name: "Planned",
                data: planData,
                stack: "planned",
                dataLabels: {
                    enabled: true,
                    verticalAlign: "top",
                    y: 15,
                    style: {
                        fontSize: "14px",
                        fontWeight: 600,
                        textOutline: "none",
                    },
                },
            },
            {
                name: "Actual",
                data: actualData,
                stack: "actual",
                dataLabels: {
                    enabled: true,
                    verticalAlign: "top",
                    y: 15,
                    style: {
                        fontSize: "14px",
                        fontWeight: 600,
                        textOutline: "none",
                    },
                },
            }
        ],

        credits: { enabled: false },
        legend: {
            enabled: true,
            align: "left",
            verticalAlign: "top",
            layout: "horizontal",
            itemDistance: 20,
            symbolRadius: 0,
            symbolHeight: 14,
            symbolWidth: 14,
            margin: 40,
            itemStyle: {
                fontWeight: 500,
                fontSize: "16px",
                color: "#182A4E",
            }
        },

        tooltip: {
            shared: true,
            useHTML: true,
            formatter: function () {
                let tooltip = `<b>Category: ${this.x}</b><br/>`; // this.x = category name

                this.points.forEach(point => {
                    tooltip += `
                <span style="color:${point.color}">\u25CF</span> 
                ${point.series.name}: <b>${point.y}</b><br/>
            `;
                });

                return tooltip;
            }
        }

    };
    const CustomChevronIcon = (props) => (
        <ChevronDownIcon style={{ width: '16px', height: '16px' }} {...props} />
    );



    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                mt: 0,
                borderRadius: "7px",
                overflow: "hidden",
                backgroundColor: "white",
                border: "1px solid #2525252E",
                p: 2
            }}
        >
            <Grid2 container>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Typography
                        variant="body"
                        sx={{
                            fontWeight: 500,
                            fontSize: "18px",
                            lineHeight: "100%",
                            letterSpacing: "-0.4px",
                            color: `${theme?.customSettings?.themeColor}`,
                        }}
                    >
                        Plan Vs Actual  (As on Date)
                    </Typography>
                    <Typography
                        sx={{
                            mt: "5px",
                            color: "#5052588A",
                            fontSize: "14px",
                            mb: "26px",
                            lineHeight: "100%",
                            letterSpacing: "-0.4px",
                        }}
                    >
                        {/* Get a holistic view of how work is being prioritized */}
                    </Typography>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Box sx={{ width: '100%', display: "flex", justifyContent: 'end' }}>
                        <Select
                            labelId="demo-simple-select-standard-label"
                            id="demo-simple-select-standard"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            // IconComponent={IoIosArrowDown}
                            IconComponent={CustomChevronIcon}
                            sx={{
                                backgroundColor: '#E8E8E8',
                                '& .MuiOutlinedInput-input': {
                                    padding: '5px 15px'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                }
                            }}
                        >
                            <MenuItem value="All Categories">All Categories</MenuItem>
                            {allCategories.map((category, idx) => (
                                <MenuItem key={category} value={category}>
                                    {category}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 12 }}>
                    {isLoading ? (
                        <Skeleton variant="rectangular" height={320} width="100%" />
                    ) : error ? (
                        <Typography color="error" sx={{ textAlign: "center", mt: 5 }}>
                            Error loading data
                        </Typography>
                    ) : isAllZero ? (
                        <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
                            <Typography variant="body1" color="textSecondary">
                                No data available to display the chart.
                            </Typography>
                        </Box>
                    ) : (
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    )}
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default PlanActualProcurement;
